import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import type { NewsletterFormData } from '../types';

const NewsletterSignup = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<NewsletterFormData>({ email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage(null);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';
      const response = await fetch(`${apiUrl}/newsletter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Subscription failed');
      }

      setMessage({
        type: 'success',
        text: t('footer.newsletter.success'),
      });
      setFormData({ email: '' });
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setMessage({
        type: 'error',
        text: t('footer.newsletter.error'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-primary">{t('footer.newsletter.title')}</h3>
      <p className="text-sm text-secondary">
        {t('footer.newsletter.desc')}
      </p>
      <form onSubmit={handleSubmit} className="space-y-3">
        <Input
          type="email"
          placeholder={t('footer.newsletter.placeholder')}
          value={formData.email}
          onChange={(e) => setFormData({ email: e.target.value })}
          required
          disabled={isSubmitting}
          className="bg-surface border-border"
        />
        <Button
          type="submit"
          variant="default"
          fullWidth
          loading={isSubmitting}
          iconName="Send"
          iconPosition="right"
          iconSize={16}
          className="gradient-cta"
        >
          {t('footer.newsletter.button')}
        </Button>
        {message && (
          <p
            className={`text-sm ${
              message.type === 'success' ? 'text-success' : 'text-error'
            }`}
          >
            {message.text}
          </p>
        )}
      </form>
    </div>
  );
};

export default NewsletterSignup;